import React, { useState, useRef } from 'react';
import { useProductContext } from '../../../context/ProductContext';

interface AddImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number;
  productName: string;
}

const AddImagesModal: React.FC<AddImagesModalProps> = ({ isOpen, onClose, productId, productName }) => {
  const { addImages, loading, error } = useProductContext();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (!validTypes.includes(file.type)) {
      return 'Only JPEG, PNG, or GIF files are allowed.';
    }
    if (file.size > maxSize) {
      return 'File size must be less than 5MB.';
    }
    return null;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const newErrors: string[] = [];
    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    files.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    setPreviews((prev) => [...prev, ...newPreviews]);
    setFileErrors(newErrors);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset input
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    URL.revokeObjectURL(previews[index]);
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      setFileErrors(['Please select at least one image.']);
      return;
    }

    try {
      await addImages(productId, selectedFiles);
      setSuccessMessage('Images added successfully!');
      setSelectedFiles([]);
      setPreviews([]);
      setFileErrors([]);
      setTimeout(() => {
        onClose();
        setSuccessMessage(null);
      }, 1500);
    } catch (err) {
      // Error handled in ProductContext
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setPreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setFileErrors([]);
    setSuccessMessage(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-images-title"
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h2 id="add-images-title" className="text-lg font-semibold mb-4">
          Add Images for {productName}
        </h2>
        {error && (
          <div className="text-red-600 bg-red-50 p-3 rounded border border-red-200 mb-4" role="alert">
            ⚠️ {error}
          </div>
        )}
        {successMessage && (
          <div className="text-green-600 bg-green-50 p-3 rounded border border-green-200 mb-4" role="alert">
            ✅ {successMessage}
          </div>
        )}
        {fileErrors.length > 0 && (
          <div className="text-red-600 bg-red-50 p-3 rounded border border-red-200 mb-4" role="alert">
            <ul className="list-disc pl-5">
              {fileErrors.map((err, index) => (
                <li key={index}>{err}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="mb-4">
          <label
            htmlFor="image-upload"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Select Images
          </label>
          <input
            id="image-upload"
            type="file"
            accept="image/jpeg,image/png,image/gif"
            multiple
            onChange={handleFileChange}
            ref={fileInputRef}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
            aria-describedby="image-upload-help"
          />
          <p id="image-upload-help" className="text-xs text-gray-500 mt-1">
            Upload JPEG, PNG, or GIF files (max 5MB each).
          </p>
        </div>
        {previews.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Images</h3>
            <div className="grid grid-cols-3 gap-2">
              {previews.map((preview, index) => (
                <div key={index} className="relative">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-24 object-cover rounded"
                  />
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    aria-label={`Remove image ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
            aria-label="Cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Upload Images"
            disabled={loading || selectedFiles.length === 0}
          >
            {loading ? 'Uploading...' : 'Upload Images'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddImagesModal;